import type { ReactNode } from 'react';
import { Flex, Layout, theme } from 'antd';
import { Logotipo } from './Logotipo';
import { SeletorDeTema } from './SeletorDeTema';

/**
 * A moldura das telas de autenticação, comum ao app e ao backoffice.
 *
 * Uma coluna só, centralizada na tela, **sem cartão**. A escolha é da Voa e o
 * motivo é bom: um cartão desenha uma caixa em volta do formulário, e ali não
 * há nada de que separá-lo - a página inteira é o formulário. A borda só rouba
 * largura útil no celular, que é onde a maior parte dos alunos chega.
 *
 * O fundo é o do contêiner, e não o do layout: sem cartão, o cinza de fundo do
 * Antd viraria a cor da tela toda, e o campo de texto branco em cima dele
 * pareceria um recorte esquecido.
 *
 * O seletor de tema fica solto no canto, fora do fluxo da coluna. Numa versão
 * anterior ele dividia uma linha com o nome do produto, no topo do formulário -
 * quer dizer, o primeiro elemento da tela de entrada era um controle de
 * aparência. Ele continua ao alcance de quem procura, e sai da frente de quem
 * veio entrar.
 *
 * **Mora no pacote comum porque as duas portas do produto são a mesma porta.**
 * O aluno entra em `app.` e o time em `admin.`, com a mesma identidade e o
 * mesmo cookie; se as telas divergissem, quem trabalha nos dois lados sentiria
 * a diferença como defeito.
 */
export function MolduraDeAutenticacao({ children }: { children: ReactNode }) {
  const { token } = theme.useToken();

  return (
    <Layout
      style={{
        minHeight: '100dvh',
        background: token.colorBgContainer,
        position: 'relative',
      }}
    >
      <div
        style={{
          position: 'absolute',
          insetBlockStart: token.padding,
          insetInlineEnd: token.padding,
          zIndex: 1,
        }}
      >
        <SeletorDeTema />
      </div>

      <Layout.Content
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          /*
           * O respiro vertical é maior que o horizontal para o bloco não colar
           * no seletor de tema quando a tela é baixa - em celular deitado, a
           * coluna centralizada sobe até o canto.
           */
          padding: `${token.paddingXL}px ${token.padding}px`,
        }}
      >
        <Flex vertical gap={token.margin} style={{ width: '100%', maxWidth: 400 }}>
          {/*
            O logotipo é a primeira coisa da coluna, alinhado à esquerda com o
            resto. Centralizá-lo pareceria capa; à esquerda, ele é o começo da
            leitura - que é o que a Voa faz, e o que faz a tela parecer um
            documento em vez de um pop-up.

            **A `div` em volta não é sobra.** O `Logotipo` é uma `img`, e como
            filha direta de uma coluna flex ela é esticada pelo
            `align-items: stretch`: a largura `auto` resolve para os 400px da
            coluna e a marca sai achatada em 400 x 32. Dentro de um bloco, a
            imagem volta a calcular a largura pela proporção.
          */}
          <div>
            <Logotipo altura={32} />
          </div>

          {children}
        </Flex>
      </Layout.Content>
    </Layout>
  );
}
